import { useState, useEffect, useCallback, CSSProperties } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API_BASE } from '../config';
import { api } from '../api';
import { useTheme } from '../context/ThemeContext';
import { useSocket } from '../context/SocketContext';
import { useAlert } from '../hooks/useAlert';
import { useToast } from '../context/ToastContext';
import { SkeletonCard } from '../components/Skeleton';
import { EmptyState, IconButton } from '../components/UI';
import Modal from '../components/Modal';
import Icon from '../components/Icon';
import SupportChat from '../components/SupportChat';
import FirstDepositBonusBanner from '../components/FirstDepositBonusBanner';
import FirstDepositBonusPopup from '../components/FirstDepositBonusPopup';

interface HomeContentItem {
  id: string;
  type: string;
  title: string;
  body: string | null;
  imageUrl: string | null;
  createdAt?: string;
}

interface UserProfile { fullName: string; }
interface WalletSummary { balance: number; }
interface HappyHour {
  id: string;
  name: string | null;
  startAt: string;
  endAt: string;
  bonusPercent: number;
  status: 'upcoming' | 'active';
  imageUrl: string | null;
}

const TYPE_ICON: Record<string, { icon: string }> = {
  promo: { icon: 'tag' },
  event: { icon: 'star' },
  announcement: { icon: 'bullhorn' },
  quote: { icon: 'quote-right' },
};

export default function Home() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { colors, isDark } = useTheme();
  const { happyHourUpdateCount } = useSocket();
  const { alert } = useAlert();
  const toast = useToast();

  const [content, setContent] = useState<HomeContentItem[]>([]);
  const [user, setUser] = useState<UserProfile | null>(null);
  const [wallet, setWallet] = useState<WalletSummary | null>(null);
  const [hideBalance, setHideBalance] = useState(false);
  const [loading, setLoading] = useState(true);
  const [showAnnouncements, setShowAnnouncements] = useState(false);

  const [showPromo, setShowPromo] = useState(false);
  const [promoInput, setPromoInput] = useState('');
  const [promoSubmitting, setPromoSubmitting] = useState(false);
  const [showReferral, setShowReferral] = useState(false);
  const [referral, setReferral] = useState<{ code: string; pendingCount: number; paidCount: number; totalEarned: number; rewardAmount: string } | null>(null);
  const [chatOpen, setChatOpen] = useState(false);
  const [happyHour, setHappyHour] = useState<HappyHour | null>(null);

  const fetchData = useCallback(async () => {
    try {
      const [contentRes, userRes, walletRes] = await Promise.allSettled([
        axios.get<HomeContentItem[]>(`${API_BASE}/home-content`),
        api.get<UserProfile>('/auth/me'),
        api.get<WalletSummary>('/wallet/me'),
      ]);
      if (contentRes.status === 'fulfilled') setContent(contentRes.value.data);
      if (userRes.status === 'fulfilled') setUser(userRes.value.data);
      if (walletRes.status === 'fulfilled') setWallet(walletRes.value.data);
    } catch { /* silent */ }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const fetchHappyHour = useCallback(async () => {
    try {
      const res = await axios.get<HappyHour | null>(`${API_BASE}/happy-hours/current`);
      setHappyHour(res.data ?? null);
    } catch { /* public best-effort */ }
  }, []);

  // Mount + realtime: re-fetch whenever the server broadcasts an update so the
  // banner + "Happy Hour: On" pill appear/clear in real time.
  useEffect(() => { fetchHappyHour(); }, [fetchHappyHour, happyHourUpdateCount]);

  const happyHourActive = happyHour?.status === 'active' ? happyHour : null;

  // When active, schedule a refetch at endAt so the banner/pill clear once the
  // window closes (no manual refresh needed).
  useEffect(() => {
    if (!happyHourActive) return;
    const ms = new Date(happyHourActive.endAt).getTime() - Date.now();
    if (ms <= 0) { fetchHappyHour(); return; }
    // setTimeout clamps very large delays; cap to ~24h then it re-arms via re-render.
    const timer = setTimeout(fetchHappyHour, Math.min(ms, 24 * 60 * 60 * 1000));
    return () => clearTimeout(timer);
  }, [happyHourActive, fetchHappyHour]);

  const fetchReferral = useCallback(async () => {
    try {
      const res = await api.get('/me/referral');
      setReferral(res.data);
    } catch { /* ignore */ }
  }, []);

  const submitPromo = async () => {
    const code = promoInput.trim().toUpperCase();
    if (!code) return;
    setPromoSubmitting(true);
    try {
      const res = await api.post('/promo/redeem', { code });
      setShowPromo(false);
      setPromoInput('');
      alert(t('home.promo.successTitle'), t('home.promo.successMessage', { amount: res.data.amount, newBalance: res.data.newBalance.toFixed(2) }));
      fetchData();
    } catch (err: any) {
      const msg = err?.response?.data?.message ?? t('home.promo.failedDefault');
      alert(t('home.promo.failedTitle'), String(msg));
    } finally {
      setPromoSubmitting(false);
    }
  };

  const openReferral = async () => {
    setShowReferral(true);
    if (!referral) await fetchReferral();
  };

  const shareReferralCode = async () => {
    if (!referral?.code) return;
    const url = `https://allgamearena.com/r/${referral.code}`;
    const shareText = t('home.referral.shareText', { code: referral.code, url });
    try {
      if ((navigator as any).share) {
        await (navigator as any).share({ title: t('home.referral.shareTitle'), text: shareText });
      } else {
        await navigator.clipboard.writeText(shareText);
        toast.success(t('home.referral.copiedTitle'), t('home.referral.copiedMessage'));
      }
    } catch { /* ignored */ }
  };

  const balanceText = wallet ? `$ ${wallet.balance.toFixed(2)}` : '$ 0.00';
  const walletGradient = isDark
    ? 'linear-gradient(135deg, #05121f 0%, #073b3f 42%, #0f766e 72%, #14f1d1 100%)'
    : 'linear-gradient(135deg, #031f1d 0%, #07505a 42%, #0f9f95 72%, #34d399 100%)';
  const walletOverlay = 'linear-gradient(135deg, rgba(255,255,255,0.24) 0%, rgba(255,255,255,0.02) 50%, rgba(2,6,23,0.20) 100%)';
  const walletIconColor = isDark ? '#99f6e4' : '#67e8f9';

  return (
    <div style={{ minHeight: '100dvh', backgroundColor: colors.bg, position: 'relative' }}>
      <div style={{
        padding: 20, paddingBottom: 40,
        paddingTop: 'calc(20px + env(safe-area-inset-top, 0px))',
      }}>
        {loading ? (
          <>
            <SkeletonCard />
            <SkeletonCard />
            <SkeletonCard />
          </>
        ) : (
          <>
            {/* Header */}
            <div style={{
              display: 'flex', justifyContent: 'space-between', alignItems: 'center',
              marginBottom: 16,
            }}>
              <div style={{
                width: 48, height: 48, borderRadius: 24, backgroundColor: '#000',
                overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                <img src="/icons/icon-512.png" alt="" style={{ width: 38, height: 38, objectFit: 'contain' }} />
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                {happyHourActive && (
                  <div style={{
                    display: 'inline-flex', alignItems: 'center', gap: 5,
                    padding: '6px 10px', borderRadius: 999,
                    backgroundColor: colors.accentSoft,
                    border: `1px solid ${colors.accent}`,
                    fontSize: 11, fontWeight: 800, color: colors.accentText,
                    whiteSpace: 'nowrap',
                  }}>
                    <Icon name="bolt" size={11} color={colors.accentText} />
                    Happy Hour: On
                  </div>
                )}
                <IconButton
                  icon="calendar-days"
                  accessibilityLabel="Open events"
                  onPress={() => navigate('/events')}
                  backgroundColor={colors.card}
                  color={colors.text}
                />
                <IconButton
                  icon="trophy"
                  accessibilityLabel="Open leaderboard"
                  onPress={() => navigate('/leaderboard')}
                  backgroundColor={colors.card}
                  color={colors.text}
                />
                <IconButton
                  icon="bell"
                  accessibilityLabel={t('home.openNotifications')}
                  onPress={() => navigate('/notifications')}
                  backgroundColor={colors.card}
                  color={colors.text}
                />
              </div>
            </div>

            <div style={{ fontSize: 24, fontWeight: 800, color: colors.text, marginTop: 4 }}>
              {t('home.greeting', { name: user ? user.fullName.split(' ')[0] : t('home.greetingFallback') })}
            </div>
            <div style={{ fontSize: 14, color: colors.textSub, marginTop: 2, marginBottom: 18 }}>
              {t('home.welcomeBack')}
            </div>

            {/* Wallet Card */}
            <div
              data-tour="wallet-card"
              style={{
                position: 'relative',
                display: 'flex', alignItems: 'center', gap: 8,
                borderRadius: 16, padding: 18, marginBottom: 22,
                background: walletGradient,
                border: `1px solid ${colors.cardBorder}`,
                overflow: 'hidden',
              }}
            >
              <div style={{
                position: 'absolute', inset: 0,
                background: walletOverlay, pointerEvents: 'none',
              }} />
              <div style={{
                position: 'absolute', top: -46, right: -28,
                width: 156, height: 156, borderRadius: '50%',
                backgroundColor: 'rgba(20,241,209,0.30)', pointerEvents: 'none',
              }} />
              <div style={{
                position: 'absolute', bottom: -70, left: -42,
                width: 148, height: 148, borderRadius: '50%',
                backgroundColor: 'rgba(56,189,248,0.18)', pointerEvents: 'none',
              }} />
              <div style={{ flex: 1, position: 'relative' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <div style={walletIconChip}>
                    <Icon name="wallet" size={13} color={walletIconColor} />
                  </div>
                  <div style={{ fontSize: 13, fontWeight: 500, color: 'rgba(255,255,255,0.78)' }}>
                    {t('home.wallet.balance')}
                  </div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', marginTop: 4 }}>
                  <div style={{ fontSize: 26, fontWeight: 900, letterSpacing: 0.3, color: '#fff' }}>
                    {hideBalance ? '$ ••••' : balanceText}
                  </div>
                  <button
                    aria-label={hideBalance ? t('home.wallet.showBalance') : t('home.wallet.hideBalance')}
                    onClick={() => setHideBalance(v => !v)}
                    style={{
                      minWidth: 44, minHeight: 44, marginLeft: 2,
                      display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                      background: 'transparent', border: 'none', cursor: 'pointer',
                    }}
                  >
                    <Icon name={hideBalance ? 'eye' : 'eye-slash'} size={14} color="rgba(255,255,255,0.68)" />
                  </button>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 5, marginTop: 8 }}>
                  <div style={walletIconChipSm}>
                    <Icon name="shield-alt" size={11} color={walletIconColor} />
                  </div>
                  <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.68)' }}>{t('home.wallet.secure')}</div>
                </div>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginLeft: 8, position: 'relative' }}>
                <button
                  data-tour="wallet-card-withdraw"
                  onClick={() => navigate('/withdraw?mode=wallet')}
                  style={{
                    display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                    padding: '10px 14px', borderRadius: 999,
                    backgroundColor: colors.secondaryRaised, color: '#fff',
                    border: 'none', cursor: 'pointer',
                    fontWeight: 900, fontSize: 12, minHeight: 44, minWidth: 112,
                  }}
                >
                  <Icon name="arrow-down" size={12} color="#fff" />
                  {t('home.wallet.withdraw')}
                </button>
                <button
                  data-tour="wallet-card-topup"
                  onClick={() => navigate('/wallet-topup')}
                  style={{
                    display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                    padding: '10px 14px', borderRadius: 999,
                    backgroundColor: colors.accent, color: '#020617',
                    border: 'none', cursor: 'pointer',
                    fontWeight: 800, fontSize: 12, minHeight: 44, minWidth: 112,
                  }}
                >
                  <Icon name="plus" size={12} color="#020617" />
                  {t('home.wallet.topUp')}
                </button>
              </div>
            </div>

            {/* Happy Hour banner */}
            {happyHourActive && (
              <div
                onClick={() => navigate('/events')}
                style={{
                  display: 'flex', alignItems: 'center', gap: 8,
                  padding: '10px 14px', borderRadius: 12, marginBottom: 18,
                  backgroundColor: colors.accentSoft,
                  border: `1px solid ${colors.accent}`,
                  cursor: 'pointer',
                  fontSize: 13, fontWeight: 800, color: colors.accentText,
                }}
              >
                <span aria-hidden style={{ fontSize: 14 }}>⚡</span>
                <span>{happyHourActive.name ? `${happyHourActive.name} ON` : 'Happy Hour ON'} · {happyHourActive.bonusPercent}% bonus on top-ups</span>
              </div>
            )}

            {/* First-deposit "free play" promo */}
            <FirstDepositBonusBanner onClick={() => navigate('/wallet-topup')} />

            {/* Quick Actions */}
            <div style={{ fontSize: 16, fontWeight: 800, color: colors.text, marginTop: 6, marginBottom: 12 }}>
              {t('home.quickActions.title')}
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10, marginBottom: 6 }}>
              <QuickAction icon="gift" title="Events" onClick={() => navigate('/events')} tourId="qa-events" />
              <QuickAction icon="wallet" title={t('home.quickActions.walletTopUp')} onClick={() => navigate('/wallet-topup')} tourId="qa-wallet-topup" />
              <QuickAction icon="arrow-up" title={t('home.quickActions.gameTopUp')} onClick={() => navigate('/topup')} tourId="qa-game-topup" />
              <QuickAction icon="arrow-down" title={t('home.quickActions.withdraw')} onClick={() => navigate('/withdraw')} tourId="qa-withdraw" />
              <QuickAction icon="ticket-alt" title={t('home.quickActions.promoCode')} onClick={() => setShowPromo(true)} tourId="qa-promo" />
              <QuickAction icon="users" title={t('home.quickActions.referEarn')} onClick={openReferral} tourId="qa-referral" />
            </div>

            {/* Announcements */}
            <div style={{
              display: 'flex', justifyContent: 'space-between', alignItems: 'center',
              marginTop: 22, marginBottom: 12,
            }}>
              <div style={{ fontSize: 16, fontWeight: 800, color: colors.text }}>{t('home.announcements.title')}</div>
              {content.length > 0 && (
                <button
                  onClick={() => setShowAnnouncements(true)}
                  style={{
                    background: 'transparent', border: 'none', cursor: 'pointer',
                    padding: '0 4px', minHeight: 44,
                    fontSize: 13, fontWeight: 700, color: colors.accentDark,
                  }}
                >
                  {t('home.announcements.viewAll')}
                </button>
              )}
            </div>

            {content.length === 0 ? (
              <EmptyState icon="bell-slash" title={t('home.announcements.emptyTitle')} body={t('home.announcements.emptyBody')} />
            ) : (
              content.map(item => {
                const typeInfo = TYPE_ICON[item.type] || { icon: 'info-circle' };
                return (
                  <div
                    key={item.id}
                    style={{
                      display: 'flex', gap: 12,
                      padding: 14, borderRadius: 14, marginBottom: 10,
                      backgroundColor: colors.card, border: `1px solid ${colors.cardBorder}`,
                    }}
                  >
                    <div style={{
                      width: 40, height: 40, borderRadius: 12,
                      backgroundColor: colors.accentSoft,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      flexShrink: 0,
                    }}>
                      <Icon name={typeInfo.icon} size={16} color={colors.accentText} />
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{
                        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                        marginBottom: 4, gap: 8,
                      }}>
                        <div style={{
                          flex: 1, fontSize: 14, fontWeight: 800, color: colors.text,
                          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                        }}>
                          {item.title}
                        </div>
                        {item.createdAt && (
                          <div style={{ fontSize: 11, color: colors.textMuted, flexShrink: 0 }}>
                            {new Date(item.createdAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                          </div>
                        )}
                      </div>
                      {item.body && (
                        <div style={{
                          fontSize: 12, lineHeight: 1.45, color: colors.textSub,
                          display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden',
                        }}>
                          {item.body}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })
            )}
          </>
        )}
      </div>

      {/* Floating Support FAB */}
      <button
        aria-label={t('home.supportFab')}
        onClick={() => setChatOpen(true)}
        style={{
          position: 'fixed', right: 18,
          bottom: 'calc(68px + 24px + env(safe-area-inset-bottom, 0px))',
          width: 56, height: 56, borderRadius: 28,
          backgroundColor: colors.accentDark, color: '#020617',
          border: 'none', cursor: 'pointer',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          boxShadow: `0 0 18px rgba(20,241,209,0.7)`,
          animation: 'aga-glow 2.4s ease-in-out infinite',
          zIndex: 90,
        }}
      >
        <Icon name="headset" size={20} color="#020617" />
      </button>

      <style>{`
        @keyframes aga-glow {
          0%, 100% { box-shadow: 0 0 4px rgba(20,241,209,0.35); }
          50%      { box-shadow: 0 0 18px rgba(20,241,209,0.95); }
        }
      `}</style>

      {/* Announcements Modal */}
      <Modal open={showAnnouncements} onClose={() => setShowAnnouncements(false)} card={false}>
        <div style={{
          backgroundColor: colors.bg, color: colors.text,
          minHeight: '100dvh', maxHeight: '100dvh', overflowY: 'auto',
          width: '100vw', position: 'fixed', inset: 0,
        }}>
          <div style={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            padding: '16px 20px',
            paddingTop: 'calc(16px + env(safe-area-inset-top, 0px))',
            backgroundColor: colors.header, borderBottom: `1px solid ${colors.headerBorder}`,
            position: 'sticky', top: 0, zIndex: 10,
          }}>
            <div style={{ fontSize: 20, fontWeight: 900, color: colors.text }}>{t('home.announcements.title')}</div>
            <button
              aria-label={t('home.announcements.close')}
              onClick={() => setShowAnnouncements(false)}
              style={{
                width: 44, height: 44, borderRadius: 22,
                background: 'transparent', border: 'none', cursor: 'pointer',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}
            >
              <Icon name="times" size={18} color={colors.textSub} />
            </button>
          </div>
          <div style={{ padding: '20px 20px 40px' }}>
            {content.map(item => {
              const typeInfo = TYPE_ICON[item.type] || { icon: 'info-circle' };
              return (
                <div
                  key={item.id}
                  style={{
                    borderRadius: 14, padding: 16, marginBottom: 12,
                    backgroundColor: colors.card, border: `1px solid ${colors.cardBorder}`,
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12 }}>
                    <div style={{
                      width: 40, height: 40, borderRadius: 12,
                      backgroundColor: colors.accentSoft,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}>
                      <Icon name={typeInfo.icon} size={16} color={colors.accentText} />
                    </div>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: 16, fontWeight: 900, color: colors.text, marginBottom: 4 }}>
                        {item.title}
                      </div>
                      {item.createdAt && (
                        <div style={{ fontSize: 11, color: colors.textMuted }}>
                          {new Date(item.createdAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                        </div>
                      )}
                    </div>
                  </div>
                  {item.imageUrl && (
                    <img
                      src={item.imageUrl}
                      alt=""
                      style={{
                        width: '100%', height: 160, borderRadius: 12,
                        objectFit: 'cover', marginBottom: 12,
                        backgroundColor: '#e2e8f0',
                      }}
                    />
                  )}
                  {item.body && (
                    <div style={{ fontSize: 13, lineHeight: 1.55, color: colors.textSub, whiteSpace: 'pre-wrap' }}>
                      {item.body}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </Modal>

      {/* Promo Modal */}
      <Modal open={showPromo} onClose={() => setShowPromo(false)}>
        <div style={{ fontSize: 18, fontWeight: 800, color: colors.text, marginBottom: 6 }}>{t('home.promo.title')}</div>
        <div style={{ fontSize: 13, color: colors.textMuted, marginBottom: 16 }}>
          {t('home.promo.subtitle')}
        </div>
        <input
          placeholder={t('home.promo.placeholder')}
          autoCapitalize="characters"
          autoCorrect="off"
          value={promoInput}
          onChange={e => setPromoInput(e.target.value.toUpperCase())}
          style={{
            width: '100%', padding: '12px 14px',
            border: `1px solid ${colors.cardBorder}`, borderRadius: 10,
            backgroundColor: 'transparent', color: colors.text,
            fontSize: 16, fontWeight: 700, letterSpacing: 2,
            textAlign: 'center', marginBottom: 14, outline: 'none',
          }}
        />
        <button
          onClick={submitPromo}
          disabled={promoSubmitting}
          style={{
            width: '100%', padding: '14px 0', borderRadius: 10,
            backgroundColor: colors.accentDark, color: '#020617',
            border: 'none', fontSize: 15, fontWeight: 800,
            cursor: promoSubmitting ? 'default' : 'pointer',
            opacity: promoSubmitting ? 0.6 : 1,
          }}
        >
          {promoSubmitting ? t('home.promo.redeeming') : t('home.promo.redeem')}
        </button>
        <button
          onClick={() => setShowPromo(false)}
          style={{
            display: 'block', margin: '10px auto 0',
            background: 'transparent', border: 'none',
            color: colors.textMuted, cursor: 'pointer',
            padding: 10, minHeight: 44,
          }}
        >
          {t('home.promo.cancel')}
        </button>
      </Modal>

      {/* Referral Modal */}
      <Modal open={showReferral} onClose={() => setShowReferral(false)}>
        <div style={{ fontSize: 18, fontWeight: 800, color: colors.text, marginBottom: 6 }}>{t('home.referral.title')}</div>
        {referral ? (
          <>
            <div style={{ fontSize: 13, color: colors.textMuted, marginBottom: 16 }}>
              {t('home.referral.subtitle', { amount: referral.rewardAmount })}
            </div>
            <div style={{
              border: `2px solid ${colors.accentDark}`,
              borderRadius: 12, padding: '18px 0',
              textAlign: 'center', marginBottom: 16,
            }}>
              <div style={{ fontSize: 22, fontWeight: 800, letterSpacing: 4, color: colors.text }}>
                {referral.code}
              </div>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-around', marginBottom: 16 }}>
              <RefStat num={String(referral.paidCount)} label={t('home.referral.paid')} />
              <RefStat num={String(referral.pendingCount)} label={t('home.referral.pending')} />
              <RefStat num={`$${referral.totalEarned.toFixed(2)}`} label={t('home.referral.earned')} />
            </div>
            <button
              onClick={shareReferralCode}
              style={{
                width: '100%', padding: '14px 0', borderRadius: 10,
                backgroundColor: colors.accentDark, color: '#020617',
                border: 'none', fontSize: 15, fontWeight: 800, cursor: 'pointer',
              }}
            >
              {t('home.referral.shareCode')}
            </button>
          </>
        ) : (
          <div style={{ fontSize: 13, color: colors.textMuted, padding: '12px 0' }}>{t('home.referral.loading')}</div>
        )}
        <button
          onClick={() => setShowReferral(false)}
          style={{
            display: 'block', margin: '10px auto 0',
            background: 'transparent', border: 'none',
            color: colors.textMuted, cursor: 'pointer',
            padding: 10, minHeight: 44,
          }}
        >
          {t('home.referral.close')}
        </button>
      </Modal>

      <SupportChat open={chatOpen} onClose={() => setChatOpen(false)} />

      <FirstDepositBonusPopup />
    </div>
  );
}

function QuickAction({ icon, title, onClick, tourId, glow }: { icon: string; title: string; onClick: () => void; tourId?: string; glow?: boolean }) {
  const { colors } = useTheme();
  return (
    <button
      onClick={onClick}
      data-tour={tourId}
      style={{
        display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 8,
        padding: 12, borderRadius: 14,
        backgroundColor: colors.card,
        border: `1px solid ${glow ? colors.accent : colors.cardBorder}`,
        cursor: 'pointer', textAlign: 'center', minHeight: 88,
        ...(glow ? { animation: 'aga-events-glow 2.2s ease-in-out infinite' } : {}),
      }}
    >
      <div style={{
        width: 40, height: 40, borderRadius: 12,
        backgroundColor: colors.accentSoft,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        flexShrink: 0,
      }}>
        <Icon name={icon} size={18} color={colors.accentText} />
      </div>
      <div style={{ fontSize: 12, fontWeight: 800, color: colors.text, lineHeight: 1.2 }}>{title}</div>
    </button>
  );
}

function RefStat({ num, label }: { num: string; label: string }) {
  const { colors } = useTheme();
  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
      <div style={{ fontSize: 18, fontWeight: 800, color: colors.text }}>{num}</div>
      <div style={{ fontSize: 11, color: colors.textMuted, marginTop: 2 }}>{label}</div>
    </div>
  );
}

const walletIconChip: CSSProperties = {
  width: 28, height: 28, borderRadius: 8,
  backgroundColor: 'rgba(255,255,255,0.14)',
  border: '1px solid rgba(255,255,255,0.26)',
  display: 'flex', alignItems: 'center', justifyContent: 'center',
};
const walletIconChipSm: CSSProperties = {
  width: 22, height: 22, borderRadius: 6,
  backgroundColor: 'rgba(255,255,255,0.12)',
  border: '1px solid rgba(255,255,255,0.22)',
  display: 'flex', alignItems: 'center', justifyContent: 'center',
};
